import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ComparisonCollapsableOption from './options/ComparisonCollapsableOption';
import ComparisonMatrixHeader from './header/ComparisonMatrixHeader';
import ComparisonMatrixOffer from './header/ComparisonMatrixOffer';
import ComparisonMatrixOverview from './overview/ComparisonMatrixOverview';
import {
  QuoteCoverageGroup,
  QuoteQuality,
  LouiseQuote,
  isLouiseQuoteWithQuality,
  LouiseQuoteWithQuality,
} from '../../../../../../../../types/Quotes';
import { toEuro } from '../../../../../utils/stringUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/redux/reducers/rootReducer';
import BottomDrawer from '../../../../../components/default/BottomDrawer';
import { Box, HStack, useBoolean } from '@wegroup/design-system';

// Glide
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';
import { use100vh } from 'react-div-100vh';
import InsuranceCompanyLogo from '../../../../../../../common/src/components/InsuranceCompanyLogo';
import { getGuaranteeNameForKey } from '../../../../../services/productFactoryService';

interface Props {
  riskObject: string;
  quotes: LouiseQuote[];
  options: (QuoteQuality | undefined)[];
  isComparisonActive: boolean;
  setIsComparisonActive: (arg0: boolean) => void;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (arg0: string) => void;
}

const MENU_ANIMATION_MS = 250;

const MobileComparisonMatrix: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  quotes,
  options,
  isComparisonActive,
  setIsComparisonActive,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const height = use100vh();
  const calculatedHeight = height ? `${height * 0.9}px` : '75vh';

  const { t } = useTranslation();
  const { selectedOffers } = useSelector((state: RootState) => state.offers);

  const [sliderIndex, setSliderIndex] = useState(0);
  const [openOptions, setOpenOptions] = useState<string[]>([]);
  const [offersSlider, setOffersSlider] = useState<Splide>();
  const [detailsSlider, setDetailsSlider] = useState<Splide>();
  const [isSplideInit, setIsSplideInit] = useBoolean(false);

  const sharedOptions = {
    rewind: false,
    arrows: false,
  };

  const refs = useRef({});

  /**
   * Initializes the glide with the correct options
   */
  const initializeGlide = () => {
    setSliderIndex(0);
    const _offersSlider = new Splide('#offersSplide', {
      ...sharedOptions,
      focus: 'center',
      perPage: 2,
      trimSpace: false,
      flickVelocityThreshold: 1000000,
      flickPower: 0.00000001,
      fixedWidth: '27.2rem',
    });

    setOffersSlider(_offersSlider);

    const _detailsSlider = new Splide('#detailsSplide', {
      ...sharedOptions,
      pagination: false,
    });

    setDetailsSlider(_detailsSlider);

    // Fix bug with multiple pagination elements being made
    const paginationElements = [
      ...document.getElementsByClassName('splide__pagination'),
    ];
    paginationElements.forEach((node) => node.remove());

    setIsSplideInit.on();
  };

  useEffect(() => {
    if (
      detailsSlider &&
      offersSlider &&
      !detailsSlider?.State.is(detailsSlider.STATES.MOUNTED) &&
      !offersSlider?.State.is(offersSlider.STATES.MOUNTED)
    ) {
      detailsSlider.mount();
      offersSlider.sync(detailsSlider).mount();
      offersSlider.on('move', () => {
        setSliderIndex(offersSlider.index);
      });
    }
  }, [detailsSlider, offersSlider]);

  useLayoutEffect(() => {
    // Dirty fix but it seems to work. Will keep this for now.
    if (isSplideInit) setTimeout(setHeightForOverview, MENU_ANIMATION_MS);
  }, [isSplideInit]);

  useEffect(() => {
    if (isComparisonActive) {
      // Animation for drawer takes about 250ms
      // This fixes an issue where glide would not have the
      // correct width.
      setTimeout(initializeGlide, MENU_ANIMATION_MS);
    } else if (isComparisonActive === false) {
      offersSlider?.destroy();
      detailsSlider?.destroy();
      setOffersSlider(undefined);
      setDetailsSlider(undefined);
    }
  }, [isComparisonActive]);

  const setIsOpen = (option: string, shouldOpen: boolean) => {
    if (shouldOpen) setOpenOptions([...openOptions, option]);
    else {
      setOpenOptions(openOptions.filter((_option) => _option !== option));
    }
  };

  const setSliderPosition = (index: number, slider?: Splide) => {
    /** TEMPORARY DISABLED, PLEASE IGNORE IN REVIEW */
    if (!slider?.State.is(slider.STATES.MOVING)) {
      slider?.go(index);
    }
  };

  const getCoverageGroupsForQuote = (
    optionKey: string,
    subOptions: QuoteCoverageGroup[],
    quote: LouiseQuoteWithQuality,
  ) => {
    return subOptions.map((subOption) => ({
      ...subOption,
      rating:
        quote.quality
          .find(({ key }) => key === optionKey)
          ?.coverage_groups.find(({ key }) => key === subOption.key)?.rating ||
        0,
    }));
  };

  const setHeightForOverview = () => {
    let maxHeight = 0;
    Object.values(refs.current).forEach((elem) => {
      const height = (elem as HTMLDivElement).offsetHeight;
      if (height > maxHeight) maxHeight = height;
    });
    Object.values(refs.current).forEach((elem) => {
      (elem as HTMLDivElement).style.height = `${maxHeight}px`;
    });
  };

  const handleClose = () => {
    setIsComparisonActive(false);
    setIsSplideInit.off();
  };

  return (
    <BottomDrawer
      isVisible={isComparisonActive}
      onClose={handleClose}
      customHeight={calculatedHeight}
    >
      <div>
        <Box
          pos="absolute"
          top="0"
          bottom="0"
          left="0"
          right="0"
          h="100%"
          w="100%"
          bg="white"
          py="6"
          overflowY="auto"
        >
          <ComparisonMatrixHeader
            title={t('Comparing your offers')}
            description={t(
              'Swipe left or right between your offers in order to compare them to eachother',
            )}
          />

          <Box
            className="splide"
            id="offersSplide"
            sx={{
              '.splide__pagination__page': {
                transition: 'background-color 0.5s',
                width: '3',
                height: '3',
                borderRadius: 'full',
                mx: '1',
                border: 'none',
                backgroundColor: 'gray.50',
              },
              '.splide__pagination__page.is-active': {
                transition: 'background-color 0.5s',
                backgroundColor: 'primary.500',
              },
            }}
          >
            <div className="splide__track">
              <ul className="splide__list">
                {quotes.map(
                  (
                    {
                      hash_id,
                      info,
                      premium,
                      matching_score,
                      guarantee_content,
                    },
                    index,
                  ) => (
                    <HStack
                      spacing="0"
                      px="2"
                      py="6"
                      className="splide__slide"
                      opacity={index === sliderIndex ? 1 : 0.45}
                      key={hash_id}
                      onClick={() => setSliderPosition(index, offersSlider)}
                      transition="opacity 0.5s"
                    >
                      <ComparisonMatrixOffer
                        Icon={
                          <InsuranceCompanyLogo
                            insuranceCompany={info.company.key}
                          />
                        }
                        insuranceCompany={info.company?.name || t('Insurer')}
                        insuranceName={getGuaranteeNameForKey(
                          info.insurance_name,
                          guarantee_content,
                        )}
                        premium={toEuro(premium.total_premium)}
                        score={matching_score}
                        isSelected={selectedOffers[riskObject]?.includes(
                          hash_id,
                        )}
                        addToSelectedOffers={() => addToSelectedOffers(hash_id)}
                        removeFromSelectedOffers={() =>
                          removeFromSelectedOffers(hash_id)
                        }
                      />
                    </HStack>
                  ),
                )}
              </ul>
            </div>
          </Box>

          {quotes.every((quote) => isLouiseQuoteWithQuality(quote)) && (
            <div className="splide" id="detailsSplide">
              <div className="splide__track">
                <ul className="splide__list">
                  {/* // Check is done above, TS does not see it */}
                  {(quotes as LouiseQuoteWithQuality[]).map((quote, index) => {
                    return (
                      <li key={quote.hash_id} className="splide__slide">
                        <ComparisonMatrixOverview
                          quote={quote}
                          ref={(element) => (refs.current[index] = element)}
                        />
                        <Box m="4">
                          {/* // Check is done above, TS does not see it */}
                          {(options as QuoteQuality[]).map(
                            ({ key, name, coverage_groups }) => (
                              <ComparisonCollapsableOption
                                subOptions={getCoverageGroupsForQuote(
                                  key,
                                  coverage_groups,
                                  quote,
                                )}
                                isOpen={openOptions.includes(key)}
                                setIsOpen={(shouldOpen) =>
                                  setIsOpen(key, shouldOpen)
                                }
                                shouldScroll={index === sliderIndex}
                                key={`mobile_comparison_option_${key}`}
                              >
                                {name}
                              </ComparisonCollapsableOption>
                            ),
                          )}
                        </Box>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </Box>
      </div>
    </BottomDrawer>
  );
};

export default MobileComparisonMatrix;
